import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AutomaticUserAllocationComponent from './AutomaticUserAllocationComponent';
import { FakeText, Card, IconButton } from '@totalsoft/rocket-ui';
import Description from '@mui/icons-material/ViewList';
import { Grid } from '@mui/material';
import { reducer, initialState } from '../reducers/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { automaticUserAllocationValidator } from '../validators';
import { useQuery, useClientQuery, useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import {
  AUTOMATIC_USER_ALLOCATIONS_QUERY,
  DEALERS_QUERY,
  RESPONSIBLES_QUERY,
  ALLOCATION_RESPONSIBLES_QUERY,
  ERPUSERS_QUERY
} from '../queries';
import { SAVE_AUTOMATIC_USER_ALLOCATIONS } from '../mutations';
import { AUTOMATIC_USER_ALLOCATIONS_SAVED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { isValid } from '@totalsoft/pure-validations-react';

const AutomaticUserAllocationContainer = () => {
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [salesResponsibleOptions, setSalesResponsibleOptions] = useState([]);
  const [asmResponsibleOptions, setASMResponsibleOptions] = useState([]);
  const [insuranceResponsibleOptions, setInsuranceResponsibleOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [automaticUserAllocationValidation, validateAutomaticUserAllocations] = useValidation(automaticUserAllocationValidator);
  const { t } = useTranslation();
  const addToast = useToast();
  const clientQuery = useClientQuery();
  const client = useApolloClient();

  useQuery(AUTOMATIC_USER_ALLOCATIONS_QUERY, {
    onCompleted: data => {
      dispatch({ type: 'OnInitState', automaticUserAllocations: data?.automaticUserAllocations ?? [] });
    }
  });

  useQuery(RESPONSIBLES_QUERY, {
    onCompleted: data => {
      setResponsibleOptions(data?.responsibles);
    }
  });

  useQuery(ALLOCATION_RESPONSIBLES_QUERY, {
    onCompleted: data => {
      const responsibles = data?.automaticUserAllocationsResponsiblesByUserGroups;
      const salesResponsibles = responsibles?.salesResponsibles;
      const asmResponsibles = responsibles?.asmResponsibles;
      const insuranceResponsibles = responsibles?.insuranceResponsibles;
      if (salesResponsibles)
        setSalesResponsibleOptions(
          salesResponsibles.map(x => {
            return { id: x.personId, name: `${x.firstName} ${x.lastName}` };
          })
        );
      if (asmResponsibles)
        setASMResponsibleOptions(
          asmResponsibles.map(x => {
            return { id: x.personId, name: `${x.firstName} ${x.lastName}` };
          })
        );
      if (insuranceResponsibles)
        setInsuranceResponsibleOptions(
          insuranceResponsibles.map(x => {
            return { id: x.personId, name: `${x.firstName} ${x.lastName}` };
          })
        );
    }
  });

  useQuery(ERPUSERS_QUERY, {
    onCompleted: data => {
      setUserOptions(data?.ERPUsers);
    }
  });

  const [saveAutomaticUserAllocations] = useMutation(SAVE_AUTOMATIC_USER_ALLOCATIONS);

  useSubscription(AUTOMATIC_USER_ALLOCATIONS_SAVED, {
    onData: data => {
      addToast(t('AutomaticUserAllocation.SavingSucceeded'), 'success');
      const allocations = data.data.data.automaticUserAllocationSaved.automaticUserAllocations;
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'automaticUserAllocations' });
      if (allocations.length > 0)
        dispatch({
          type: 'OnSaveSuccesful',
          automaticUserAllocations: data.data.data.automaticUserAllocationSaved.automaticUserAllocations
        });
    }
  });

  const onLoadDealers = useCallback(
    (selectedDealer, selectedDealerIds) => async search => {
      const [data] = await Promise.all([
        clientQuery(DEALERS_QUERY, {
          variables: { search: selectedDealer?.name === search ? '' : search }
        }),
        new Promise(resolve => setTimeout(resolve, 1)) //workaround for issue https://github.com/osstotalsoft/rocket-ui-ts/issues/90 (todo: remove after it is fixed)
      ]);

      const options =
        data.data.dealers?.filter(x => selectedDealer?.dealerId === x.dealerId || !selectedDealerIds.includes(x.dealerId)) || [];
      if (selectedDealer && !options.some(x => x.dealerId === selectedDealer.dealerId)) options.push(selectedDealer);

      return options;
    },
    [clientQuery]
  );

  useEffect(() => {
    validateAutomaticUserAllocations(localState.automaticUserAllocationsGroupedByDealer);
  }, [localState.automaticUserAllocationsGroupedByDealer, validateAutomaticUserAllocations]);

  const onAddCard = useCallback(() => {
    dispatch({ type: 'OnAddCard' });
  }, []);

  const onAddAllocation = useCallback(
    cardIndex => () => {
      dispatch({ type: 'OnAddAllocation', cardIndex: cardIndex });
    },
    []
  );

  const onSave = useCallback(
    cardIndex => () => {
      if (!(automaticUserAllocationValidation[cardIndex] |> isValid)) return;
      const allocationsToSave = localState.automaticUserAllocationsGroupedByDealer[cardIndex].allocations.map(x => {
        return {
          automaticUserAllocationId: x.automaticUserAllocationId,
          dealerId: x.dealerId,
          userId: x.userId,
          offerResponsibleId: x.offerResponsibleId,
          offerAccountManagerId: x.offerAccountManagerId,
          offerInsuranceUserId: x.offerInsuranceUserId,
          allUsers: x.allUsers
        };
      });
      saveAutomaticUserAllocations({
        variables: {
          allocationsToSave: allocationsToSave,
          allocationIdsToDelete: localState.deletedAutomaticUserAllocationIds
        }
      });
    },
    [
      automaticUserAllocationValidation,
      localState.automaticUserAllocationsGroupedByDealer,
      localState.deletedAutomaticUserAllocationIds,
      saveAutomaticUserAllocations
    ]
  );

  const onDeleteCard = useCallback(
    cardIndex => () => {
      if (window.confirm(t('AutomaticUserAllocation.ConfirmDelete'))) {
        saveAutomaticUserAllocations({
          variables: {
            allocationsToSave: null,
            allocationIdsToDelete: localState.automaticUserAllocationsGroupedByDealer[cardIndex].allocations
              .map(x => x.automaticUserAllocationId)
              .filter(x => x !== null)
          }
        });
        dispatch({ type: 'OnDeleteCard', cardIndex: cardIndex });
      }
    },
    [localState.automaticUserAllocationsGroupedByDealer, saveAutomaticUserAllocations, t]
  );

  const onDeleteAllocation = useCallback(
    (cardIndex, allocationIndex) => () => {
      dispatch({ type: 'OnDeleteAllocation', cardIndex: cardIndex, allocationIndex: allocationIndex });
    },
    []
  );

  const onPropertyChange = useCallback((prop, value, cardIndex, allocationIndex, prop2, value2) => {
    dispatch({
      type: 'OnPropertyChange',
      prop: prop,
      value: value,
      cardIndex: cardIndex,
      allocationIndex: allocationIndex,
      prop2: prop2,
      value2: value2
    });
  }, []);

  return (
    <>
      <Prompt
        when={localState.automaticUserAllocationsGroupedByDealer.some(x => x.isDirty)}
        message={t('AutomaticUserAllocation.LeavingWithoutSaving')}
      />
      <Card
        icon={Description}
        iconColor='primary'
        title={t('AutomaticUserAllocation.Title')}
        actions={<IconButton type='add' fontSize='medium' color='transparent' tooltip={t('Tooltips.Add')} onClick={onAddCard} />}
      >
        {!localState.loaded ? (
          <FakeText lines={10} />
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {localState.automaticUserAllocationsGroupedByDealer.map((item, cardIndex) => {
              return (
                <Grid key={`${item.dealerId}-${cardIndex}`} item xs={12}>
                  <Card>
                    <AutomaticUserAllocationComponent
                      cardIndex={cardIndex}
                      automaticUserAllocationsOfDealer={item.allocations}
                      isDirty={item.isDirty}
                      dispatch={dispatch}
                      validation={automaticUserAllocationValidation}
                      onLoadDealers={onLoadDealers}
                      selectedDealerIds={localState.automaticUserAllocationsGroupedByDealer.map(x => x.dealerId)}
                      userOptions={userOptions}
                      responsibleOptions={responsibleOptions}
                      salesResponsibleOptions={salesResponsibleOptions}
                      asmResponsibleOptions={asmResponsibleOptions}
                      insuranceResponsibleOptions={insuranceResponsibleOptions}
                      onDeleteCard={onDeleteCard}
                      onAddAllocation={onAddAllocation}
                      onDeleteAllocation={onDeleteAllocation}
                      onSave={onSave}
                      onPropertyChange={onPropertyChange}
                    />
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Card>
    </>
  );
};

export default AutomaticUserAllocationContainer;
